import { AppUser } from 'types/application.types';
import { SegmentCondition } from 'types/push.types';

export interface SegmentSuggestion {
  id: string;
  name: string;
  description: string;
  conditions: SegmentCondition[];
  matchedUsers: number;
  totalUsers: number;
  percentage: number;
  priority: 'high' | 'medium' | 'low';
  tags: string[];
}

const MIN_GROUP_PERCENT = 5;
const MIN_GROUP_SIZE = 10;

export function generateSegmentSuggestions(
  users: AppUser[],
  existingSegmentNames?: string[],
): SegmentSuggestion[] {
  if (!users.length) return [];

  const suggestions: SegmentSuggestion[] = [];
  const total = users.length;
  const existing = new Set((existingSegmentNames || []).map(n => n.toLowerCase()));
  const byOS = groupBy(users, u => u.os || '');
  for (const [os, group] of Object.entries(byOS)) {
    if (!os) continue;
    if (isSignificant(group.length, total)) {
      suggestions.push({
        id: `os_${os}`,
        name: `${os.toUpperCase()} пользователи`,
        description: `Все пользователи на ${os.toUpperCase()} (${group.length} чел.)`,
        conditions: [{ field: 'os', operator: 'equals', value: os }],
        matchedUsers: group.length,
        totalUsers: total,
        percentage: pct(group.length, total),
        priority: group.length / total > 0.3 ? 'high' : 'medium',
        tags: ['os'],
      });
    }
  }

  const byLocale = groupBy(users, u => u.locale || '');
  for (const [locale, group] of Object.entries(byLocale)) {
    if (!locale) continue;
    if (isSignificant(group.length, total)) {
      suggestions.push({
        id: `locale_${locale}`,
        name: `Локаль ${locale.toUpperCase()}`,
        description: `Пользователи с локалью ${locale.toUpperCase()} (${group.length} чел.)`,
        conditions: [{ field: 'locale', operator: 'equals', value: locale }],
        matchedUsers: group.length,
        totalUsers: total,
        percentage: pct(group.length, total),
        priority: group.length / total > 0.3 ? 'high' : 'medium',
        tags: ['locale'],
      });
    }
  }

  const byOffer = groupBy(users, u => getNullStr(u.offer) || '');
  for (const [offer, group] of Object.entries(byOffer)) {
    if (!offer) continue;
    if (isSignificant(group.length, total)) {
      suggestions.push({
        id: `offer_${offer}`,
        name: `Оффер: ${offer}`,
        description: `Пользователи с оффером "${offer}" (${group.length} чел.)`,
        conditions: [{ field: 'offer', operator: 'equals', value: offer }],
        matchedUsers: group.length,
        totalUsers: total,
        percentage: pct(group.length, total),
        priority: group.length / total > 0.2 ? 'high' : 'medium',
        tags: ['offer'],
      });
    }
  }

  const byCountry = groupBy(users, u => getNullStr(u.country) || '');
    for (const [country, group] of Object.entries(byCountry)) {
      if (!country) continue;
      if (isSignificant(group.length, total)) {
        suggestions.push({
          id: `country_${country}`,
          name: `Гео: ${country.toUpperCase()}`,
          description: `Пользователи из ${country.toUpperCase()} (${group.length} чел.)`,
          conditions: [{ field: 'country', operator: 'equals', value: country }],
          matchedUsers: group.length,
          totalUsers: total,
          percentage: pct(group.length, total),
          priority: group.length / total > 0.3 ? 'high' : 'medium',
          tags: ['country'],
        });
      }
    }

  const withReg = users.filter(u => u.has_registration);
  const noReg = users.filter(u => !u.has_registration);
  const withDep = users.filter(u => u.has_deposit);
  const regNoDep = users.filter(u => u.has_registration && !u.has_deposit);

  if (isSignificant(withReg.length, total)) {
    suggestions.push({
      id: 'conv_registered',
      name: 'Зарегистрированные',
      description: `Пользователи с регистрацией (${withReg.length} чел.)`,
      conditions: [{ field: 'has_registration', operator: 'equals', value: true }],
      matchedUsers: withReg.length,
      totalUsers: total,
      percentage: pct(withReg.length, total),
      priority: 'high',
      tags: ['conversion'],
    });
  }

  if (isSignificant(noReg.length, total)) {
    suggestions.push({
      id: 'conv_no_reg',
      name: 'Без регистрации',
      description: `Без регистрации — потенциал для ретаргетинга (${noReg.length} чел.)`,
      conditions: [{ field: 'has_registration', operator: 'equals', value: false }],
      matchedUsers: noReg.length,
      totalUsers: total,
      percentage: pct(noReg.length, total),
      priority: 'high',
      tags: ['conversion', 'retarget'],
    });
  }

  if (isSignificant(regNoDep.length, total)) {
    suggestions.push({
      id: 'conv_reg_no_dep',
      name: 'Регистрация без депозита',
      description: `Зарегистрированы, но без депозита — ключевой сегмент для конверсии (${regNoDep.length} чел.)`,
      conditions: [
        { field: 'has_registration', operator: 'equals', value: true },
        { field: 'has_deposit', operator: 'equals', value: false },
      ],
      matchedUsers: regNoDep.length,
      totalUsers: total,
      percentage: pct(regNoDep.length, total),
      priority: 'high',
      tags: ['conversion', 'retarget'],
    });
  }

  if (isSignificant(withDep.length, total)) {
    suggestions.push({
      id: 'conv_deposited',
      name: 'С депозитом',
      description: `Пользователи, сделавшие депозит (${withDep.length} чел.)`,
      conditions: [{ field: 'has_deposit', operator: 'equals', value: true }],
      matchedUsers: withDep.length,
      totalUsers: total,
      percentage: pct(withDep.length, total),
      priority: 'medium',
      tags: ['conversion'],
    });
  }

  const byCombo = groupBy(users, u => `${u.os || ''}|${u.locale || ''}`);
  for (const [combo, group] of Object.entries(byCombo)) {
    const [os, locale] = combo.split('|');
    if (!os || !locale) continue;
    if (isSignificant(group.length, total) && group.length >= MIN_GROUP_SIZE * 2) {
      suggestions.push({
        id: `combo_${os}_${locale}`,
        name: `${os.toUpperCase()} / ${locale.toUpperCase()}`,
        description: `${os.toUpperCase()} + локаль ${locale.toUpperCase()} (${group.length} чел.)`,
        conditions: [
          { field: 'os', operator: 'equals', value: os },
          { field: 'locale', operator: 'equals', value: locale },
        ],
        matchedUsers: group.length,
        totalUsers: total,
        percentage: pct(group.length, total),
        priority: group.length / total > 0.2 ? 'high' : 'low',
        tags: ['os', 'locale', 'combo'],
      });
    }
  }

  const byOsOffer = groupBy(users, u => `${u.os || ''}|${getNullStr(u.offer) || ''}`);
  for (const [combo, group] of Object.entries(byOsOffer)) {
    const [os, offer] = combo.split('|');
    if (!os || !offer) continue;
    if (isSignificant(group.length, total) && group.length >= MIN_GROUP_SIZE * 2) {
      suggestions.push({
        id: `combo_${os}_offer_${offer}`,
        name: `${os.toUpperCase()} / ${offer}`,
        description: `${os.toUpperCase()} + оффер "${offer}" (${group.length} чел.)`,
        conditions: [
          { field: 'os', operator: 'equals', value: os },
          { field: 'offer', operator: 'equals', value: offer },
        ],
        matchedUsers: group.length,
        totalUsers: total,
        percentage: pct(group.length, total),
        priority: group.length / total > 0.15 ? 'medium' : 'low',
        tags: ['os', 'offer', 'combo'],
      });
    }
  }

  const byCountryRegNoDep = groupBy(
      users.filter(u => u.has_registration && !u.has_deposit),
      u => getNullStr(u.country) || '',
    );
    for (const [country, group] of Object.entries(byCountryRegNoDep)) {
      if (!country) continue;
      if (group.length >= MIN_GROUP_SIZE && (group.length / total) * 100 >= 2) {
        suggestions.push({
          id: `combo_country_${country}_reg_no_dep`,
          name: `${country.toUpperCase()}: рег. без депа`,
          description: `${country.toUpperCase()}, зарегистрированы, но без депозита — приоритет на конверсию (${group.length} чел.)`,
          conditions: [
            { field: 'country', operator: 'equals', value: country },
            { field: 'has_registration', operator: 'equals', value: true },
            { field: 'has_deposit', operator: 'equals', value: false },
          ],
          matchedUsers: group.length,
          totalUsers: total,
          percentage: pct(group.length, total),
          priority: 'high',
          tags: ['country', 'conversion', 'retarget', 'combo'],
        });
      }
    }

  const filtered = suggestions.filter(s => !existing.has(s.name.toLowerCase()));

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  filtered.sort((a, b) => {
    const p = priorityOrder[a.priority] - priorityOrder[b.priority];
    return p !== 0 ? p : b.percentage - a.percentage;
  });

  return filtered;
}

function groupBy<T>(items: T[], keyFn: (item: T) => string): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of items) {
    const key = keyFn(item);
    if (!result[key]) result[key] = [];
    result[key].push(item);
  }
  return result;
}

function isSignificant(count: number, total: number): boolean {
  return count >= MIN_GROUP_SIZE && (count / total) * 100 >= MIN_GROUP_PERCENT;
}

function pct(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 1000) / 10 : 0;
}

function getNullStr(value: any): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value || null;
  if (typeof value === 'object' && 'String' in value && 'Valid' in value) {
    return value.Valid ? value.String : null;
  }
  return null;
}